import { Tile } from '../tiles';
import { toCounts } from '../hand';
import { isAgari } from '../agari';
import { isFuriten, isTenpai } from '../waits';
import { countDora } from '../dora';
import { scoreWin, ScoreContext } from '../scoring/win';
import { GameState, GameAction } from './types';

/** 화료 점수 문맥 구성 (도라 포함). */
export function buildScoreContext(
  state: GameState,
  seat: number,
  winningTile: Tile,
  isTsumo: boolean,
): ScoreContext {
  const p = state.players[seat];
  // 화료 손패(14장): 닫힌 손패(13) + 화료패. (쯔모의 drawn은 hand에 미포함이므로 동일하게 더한다.)
  const fullTiles = [...p.hand, winningTile];
  const dora = countDora(fullTiles, state.doraIndicators);
  return {
    winningTile,
    isTsumo,
    isRiichi: p.riichi,
    seatWind: p.seat,
    roundWind: state.roundWind,
    isDealer: seat === state.dealer,
    doraCount: dora,
  };
}

/** 화료 손패 카운트(14장) — 쯔모/론 공통. seat의 닫힌 손패 + 화료패. */
function winningCounts(state: GameState, seat: number, winningTile: Tile): number[] {
  return toCounts([...state.players[seat].hand, winningTile]);
}

export function canTsumo(state: GameState, seat: number): boolean {
  if (state.phase !== 'discard' || state.turn !== seat || state.drawn === null) return false;
  const counts = winningCounts(state, seat, state.drawn);
  if (!isAgari(counts)) return false;
  const ctx = buildScoreContext(state, seat, state.drawn, true);
  return scoreWin(counts, ctx).valid;
}

export function canRon(state: GameState, seat: number): boolean {
  if (state.phase !== 'response' || !state.responders.includes(seat)) return false;
  if (state.lastDiscard === null) return false;
  const tile = state.lastDiscard.tile;
  const counts = winningCounts(state, seat, tile);
  if (!isAgari(counts)) return false;
  if (isFuriten(state.players[seat].hand, state.players[seat].discards)) return false;
  const ctx = buildScoreContext(state, seat, tile, false);
  return scoreWin(counts, ctx).valid;
}

export function canRiichi(state: GameState, seat: number): boolean {
  if (state.phase !== 'discard' || state.turn !== seat || state.drawn === null) return false;
  const p = state.players[seat];
  if (p.riichi || p.points < 1000) return false;
  if (state.wall.length < 4) return false; // 리치 선언엔 최소 한 번의 쯔모 기회(라이브 산 ≥4) 필요
  // 14장 중 한 장을 버려 텐파이가 유지되는 버림이 하나라도 있으면 리치 가능
  const all = [...p.hand, state.drawn];
  for (let i = 0; i < all.length; i++) {
    const rest = all.slice(0, i).concat(all.slice(i + 1));
    if (isTenpai(rest)) return true;
  }
  return false;
}

/** 현재 phase/turn에 맞는 합법 행동 전체. */
export function legalActions(state: GameState, seat: number): GameAction[] {
  if (state.phase === 'ended') return [];

  if (state.phase === 'draw') {
    return state.turn === seat ? [{ type: 'draw' }] : [];
  }

  if (state.phase === 'discard') {
    if (state.turn !== seat || state.drawn === null) return [];
    const acts: GameAction[] = [];
    if (canTsumo(state, seat)) acts.push({ type: 'tsumo' });
    const p = state.players[seat];
    const all = [...p.hand, state.drawn];
    if (p.riichi) {
      // 리치 후엔 가져온 패만 버림(쯔모기리)
      acts.push({ type: 'discard', tile: state.drawn });
    } else {
      const seen = new Set<Tile>();
      for (const tile of all) {
        if (seen.has(tile)) continue;
        seen.add(tile);
        acts.push({ type: 'discard', tile });
      }
      if (canRiichi(state, seat)) {
        // 리치 후보 버림: 텐파이 유지되는 패만
        const seenR = new Set<Tile>();
        for (let i = 0; i < all.length; i++) {
          const tile = all[i];
          if (seenR.has(tile)) continue;
          seenR.add(tile);
          const rest = all.slice(0, i).concat(all.slice(i + 1));
          if (isTenpai(rest)) acts.push({ type: 'riichi', tile });
        }
      }
    }
    return acts;
  }

  // response
  if (state.phase === 'response') {
    if (!state.responders.includes(seat)) return [];
    const acts: GameAction[] = [{ type: 'pass', player: seat }];
    if (canRon(state, seat)) acts.push({ type: 'ron', player: seat });
    return acts;
  }

  return [];
}
